/**
 * 🩹 PATCH-350 (2026-02) · Terms of Service
 * ------------------------------------------
 * Governing agreement between LIFE LLC (operator) and every user of
 * the Platform. Split into three tiers of terms because our user
 * classes have materially different obligations: public site
 * visitors, program participants (residents), and partner
 * organizations (employers/vendors/landlords).
 */
import LegalPageShell from "../components/LegalPageShell";

export default function TermsOfService() {
  return (
    <LegalPageShell
      slug="terms"
      eyebrow="Legal · Terms"
      title="Terms of Service"
      subtitle="The rules of the road for using the theexchangealliance.com Platform and any related portal."
      lastUpdated="2026-02-25"
    >
      <div className="callout">
        <strong>Acceptance</strong>
        By accessing theexchangealliance.com, the Personal Advancement and Career Training, the
        Resident Portal, Vendor Portal, Landlord Portal, Employer Portal, or any
        other surface operated by <em>The Learning Institute for Empowerment, LLC</em>
        {" "}("LIFE LLC"), you accept these Terms.
      </div>

      <h2>1. Definitions</h2>
      <ul>
        <li><strong>Platform</strong> — theexchangealliance.com and every branded portal and API operated by LIFE LLC.</li>
        <li><strong>User</strong> — any person or organization accessing the Platform.</li>
        <li><strong>Resident</strong> — a program participant enrolled in The Exchange Alliance's housing / vocational program.</li>
        <li><strong>Partner</strong> — an employer, landlord, service vendor, or governmental partner integrated with the Platform.</li>
      </ul>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 18 years of age (or an emancipated minor, or a
        participant with documented guardian consent) to create an account. By
        agreeing to these Terms, you represent that you have the legal capacity to
        do so on behalf of yourself or the organization you represent.
      </p>

      <h2>3. Account Security</h2>
      <ul>
        <li>You are responsible for maintaining the confidentiality of your credentials.</li>
        <li>Multi-Factor Authentication (MFA) is <strong>required</strong> for all staff accounts and available (optionally required) for partner accounts.</li>
        <li>You must notify us within 24 hours of any suspected unauthorized access via <a href="mailto:security@theexchangealliance.org">security@theexchangealliance.org</a>.</li>
        <li>LIFE LLC is not liable for losses caused by unauthorized use of your credentials where you failed to notify us of a suspected breach.</li>
      </ul>

      <h2>4. Acceptable Use</h2>
      <p>You agree <strong>not</strong> to:</p>
      <ul>
        <li>Attempt to bypass authentication, authorization, or rate-limiting mechanisms.</li>
        <li>Scrape, mirror, or ingest Platform content into an AI training corpus without written permission.</li>
        <li>Upload malicious code, spam, or content that violates state / federal law.</li>
        <li>Access another user's records without documented authorization.</li>
        <li>Extract Protected Health Information (PHI) outside the terms of an executed BAA.</li>
        <li>Impersonate LIFE LLC, its officers, or its partners.</li>
      </ul>
      <p>Violation may result in account termination, referral to law enforcement, and civil action.</p>

      <h2>5. Program Participants (Residents)</h2>
      <p>
        Residents enrolled in The Exchange Alliance program agree to a separate
        Participant Enrollment Agreement, which supplements — but does not
        replace — these Terms. The Enrollment Agreement governs housing rules,
        vocational obligations, milestone requirements, and grounds for
        discharge. Copies are provided at intake and available on request from
        your case manager.
      </p>

      <h2>6. Partner Organizations</h2>
      <p>
        Employers, landlords, and vendors accessing partner portals agree to:
      </p>
      <ul>
        <li>Provide accurate business identity and W-9 details.</li>
        <li>Handle any resident data received under the minimum-necessary standard.</li>
        <li>Execute a <a href="/legal/baa">Business Associate Agreement</a> before receiving any Protected Health Information.</li>
        <li>Report suspected data incidents within 24 hours of discovery.</li>
      </ul>

      <h2>7. Intellectual Property</h2>
      <p>
        The Platform, including PACT software, brand marks (LIFE LLC, The Exchange
        Alliance, theexchangealliance.com), the Program Management Committee methodology, and
        all supporting content, is the property of LIFE LLC and its chartering
        non-profit. Nothing in these Terms transfers ownership.
      </p>
      <p>
        Content you upload (submitted documents, photos, testimony, memos) is
        licensed to LIFE LLC solely to operate the program and preserve required
        records. You retain ownership of your content.
      </p>

      <h2>8. Donations</h2>
      <p>
        Donations processed through the Platform are collected on behalf of the
        the non-profit collaborative. Donors receive tax receipts under
        the applicable 501(c)(3) status. Donations are non-refundable except in
        cases of documented processing error.
      </p>

      <h2>9. Disclaimers</h2>
      <p>
        The Platform is provided "as is" and "as available." LIFE LLC does not
        warrant uninterrupted access, error-free operation, or fitness for any
        particular purpose beyond the express commitments in an executed BAA,
        MSA, or Participant Enrollment Agreement.
      </p>

      <h2>10. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, LIFE LLC's aggregate liability
        for any claim arising out of the Platform is limited to the greater of
        (a) fees actually paid by the claimant in the twelve (12) months
        preceding the claim, or (b) one thousand US dollars ($1,000). This
        limitation does not apply to LIFE LLC's obligations under an executed
        BAA, indemnification obligations, or gross negligence.
      </p>

      <h2>11. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless LIFE LLC, its officers,
        directors, and partner agencies from claims arising from your breach
        of these Terms, your misuse of the Platform, or your unauthorized
        disclosure of another user's information.
      </p>

      <h2>12. Termination</h2>
      <p>
        Either party may terminate access at any time. Grounds for immediate
        termination by LIFE LLC include violation of § 4 (Acceptable Use),
        non-payment (where applicable), or a court order. Termination does not
        relieve either party of confidentiality or data-retention obligations.
      </p>

      <h2>13. Governing Law &amp; Dispute Resolution</h2>
      <p>
        These Terms are governed by the laws of the State of South Carolina,
        without regard to its conflicts-of-law provisions. Disputes will be
        resolved first through good-faith negotiation, then non-binding
        mediation in Horry County, SC. If mediation fails, disputes proceed to
        binding arbitration under the American Arbitration Association's
        Commercial Rules, except that either party may seek injunctive relief
        in state or federal court for intellectual-property or data-breach
        matters.
      </p>

      <h2>14. Changes to These Terms</h2>
      <p>
        We may revise these Terms. Material changes will be posted with an
        updated "Last updated" date and, for logged-in users, an in-app notice
        requiring acknowledgment. Continued use of the Platform after a
        material change constitutes acceptance.
      </p>

      <h2>15. Contact</h2>
      <p><strong>The Learning Institute for Empowerment, LLC</strong> (DBA LIFE LLC)</p>
      <ul>
        <li>General inquiries: <a href="mailto:info@theexchangealliance.org">info@theexchangealliance.org</a></li>
        <li>Legal: <a href="mailto:legal@theexchangealliance.org">legal@theexchangealliance.org</a></li>
        <li>Security: <a href="mailto:security@theexchangealliance.org">security@theexchangealliance.org</a></li>
        <li>Mailing Address: 7433 Springside Drive, Myrtle Beach, SC 29588</li>
      </ul>
    </LegalPageShell>
  );
}
